import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';

const BulkImport = ({ onBack }) => {
  const [jsonInput, setJsonInput] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const { toast } = useToast();

  const handleImport = async () => {
    try {
      const lessons = JSON.parse(jsonInput);
      
      if (!Array.isArray(lessons)) {
        toast({
          title: "Invalid Format",
          description: "JSON must be an array of lessons",
          variant: "destructive"
        });
        return;
      }

      setImporting(true);
      const token = localStorage.getItem('adminToken');
      
      const response = await api.post('/admin/lessons/bulk-import', 
        { lessons },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setResult(response.data);
      
      toast({
        title: "Import Complete",
        description: `Imported: ${response.data.imported}, Updated: ${response.data.updated}`
      });

    } catch (error) {
      if (error instanceof SyntaxError) {
        toast({
          title: "Invalid JSON",
          description: "Please check your JSON syntax",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Import Failed",
          description: error.response?.data?.detail || "Failed to import lessons",
          variant: "destructive"
        });
      }
    } finally {
      setImporting(false);
    }
  };

  const sampleJSON = `[
  {
    "lesson_id": "day15",
    "day_number": 15,
    "passage_id": "eph4",
    "session_id": "eph4_session",
    "title": "Sample Lesson",
    "description": "A sample lesson for import",
    "verses": "1",
    "prayer_checklist": [
      "Praise God",
      "Give Thanks to Him",
      "Ask Him to reveal His will and teach you"
    ],
    "closing_prayer": [
      "Listen for God to speak",
      "Consider how you will be obedient"
    ],
    "scripture": "Therefore i, a prisoner for serving the Lord, beg you to lead a life worthy of your calling, for you have been called by God.",
    "reference": "Ephesians 4:1",
    "key_words": ["prisoner", "calling", "worthy"],
    "memorization": [
      {
        "verse": 1,
        "abbreviated": "Therefore i, a prisoner... lead a life worthy of your calling..."
      }
    ],
    "activities": [
      {
        "verse": 1,
        "text": "Therefore ___, a ___ for serving the Lord, beg you to lead a ___ worthy of your calling.",
        "answers": ["i", "prisoner", "life"]
      }
    ],
    "trivia": [
      {
        "question": "Who is the writer addressing in verse 1?",
        "answer": "believers",
        "reference": "E 4:1",
        "points": 10
      }
    ]
  }
]`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Bulk Import</h1>
          <Button onClick={onBack} variant="outline">
            Back
          </Button>
        </div>

        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Import Instructions</CardTitle>
              <CardDescription>
                Paste your JSON array of lessons below. Each lesson will be created or updated.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Required Fields:</h3>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    <li>lesson_id (string) - unique identifier like "day1", "day2"</li>
                    <li>day_number (integer) - lesson sequence number</li>
                    <li>passage_id (string) - typically "eph4"</li>
                    <li>session_id (string) - study session ID like "eph4_session"</li>
                    <li>title (string) - lesson title</li>
                    <li>verses (string) - verse range like "1-3"</li>
                    <li>prayer_checklist (array) - prayer items</li>
                    <li>closing_prayer (array) - closing prayer items</li>
                    <li>scripture (string) - full scripture text</li>
                    <li>reference (string) - Bible reference</li>
                    <li>key_words (array) - important words to select</li>
                    <li>memorization (array) - memorization objects with verse and abbreviated</li>
                    <li>activities (array) - activity objects with verse, text, answers</li>
                    <li>trivia (array) - trivia question objects with question, answer, reference, points</li>
                  </ul>
                </div>
                <div>
                  <Button
                    onClick={() => setJsonInput(sampleJSON)}
                    variant="outline"
                    size="sm"
                  >
                    Load Sample JSON
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>JSON Data</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                rows={20}
                className="font-mono text-sm"
                placeholder="Paste your JSON array here..."
              />
              <div className="mt-4 flex gap-2">
                <Button 
                  onClick={handleImport} 
                  disabled={importing || !jsonInput}
                  className="flex-1"
                >
                  {importing ? 'Importing...' : 'Import Lessons'}
                </Button>
                <Button 
                  onClick={() => {
                    setJsonInput('');
                    setResult(null);
                  }} 
                  variant="outline"
                >
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          {result && (
            <Card>
              <CardHeader>
                <CardTitle>Import Results</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p><strong>Imported:</strong> {result.imported}</p>
                  <p><strong>Updated:</strong> {result.updated}</p>
                  <p><strong>Total Processed:</strong> {result.total_processed}</p>
                  {result.errors && result.errors.length > 0 && (
                    <div className="mt-4">
                      <p className="text-red-600 font-semibold">Errors:</p>
                      <ul className="list-disc list-inside text-sm text-red-600">
                        {result.errors.map((err, idx) => (
                          <li key={idx}>
                            {err.lesson}: {err.error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkImport;
